import { useQuery, useMutation, useReactiveVar } from '@apollo/client/react';
import AddBoxIcon from '@mui/icons-material/AddBoxOutlined';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Button } from '@mui/material';
import { type Dayjs } from 'dayjs';
import { EssealDataTable } from 'esseal-data-table';
import { some } from 'lodash';
import React, { useState, useContext, useEffect } from 'react';

import tasksTableColumns from './tasksTableColumns';

import { type TasksFiltersType } from '.';

import ShowTaskModal from '@/components/Tasks/ShowTaskModal';
import CurrentUserContext from '@/Contexts/CurrentUser';
import PermissionContext from '@/Contexts/Permissions';
import {
  GetTasksDocument,
  type TaskEntity,
  DestroyTaskDocument,
  type Task,
  PermissionActionEnum,
  PermissionEntityEnum,
} from '@/graphql/generated/graphql';
import { setOpenedModals } from '@/graphql/reactiveVars';
import { objectLength } from '@/utils/helperFunctions';

interface TasksListTableProps {
  setShowFilters: React.Dispatch<React.SetStateAction<boolean>>;
  filters: TasksFiltersType;
}

const TasksListTable: React.FC<TasksListTableProps> = ({ setShowFilters, filters }) => {
  const { currentUser } = useContext(CurrentUserContext);
  const { canPerformAction } = useContext(PermissionContext);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const openedModal = useReactiveVar(setOpenedModals);

  const { data, loading } = useQuery(GetTasksDocument, {
    fetchPolicy: 'cache-and-network',
    skip: !currentUser?.defaultCompany?.id,
    variables: {
      ...filters,
      dueDateFrom: filters.dueDateFrom ? filters.dueDateFrom.toISOString() : null,
      dueDateTo: filters.dueDateTo ? filters.dueDateTo.toISOString() : null,
    },
  });

  useEffect(() => {
    if (data?.getTasks) {
      setShowFilters(
        objectLength(data.getTasks) > 0 || some(filters, (value: string | Dayjs | TaskEntity | null) => value !== ''),
      );
    }
    //eslint-disable-next-line
  }, [data?.getTasks]);

  const [destroyTask] = useMutation(DestroyTaskDocument, {
    refetchQueries: ['getTasks'],
  });

  const canDisableEditButton = (task: Task) => {
    return !(
      canPerformAction(PermissionActionEnum.Manage, PermissionEntityEnum.Tasks) ||
      task.assignee.id === currentUser?.id ||
      task.creator.id === currentUser?.id
    );
  };

  return (
    <React.Fragment>
      <EssealDataTable
        rows={(data?.getTasks as Task[]) || []}
        columns={tasksTableColumns}
        loading={loading}
        pagination={true}
        pageSize={25}
        rowActions={(row) => [
          {
            label: 'Show Task',
            icon: <VisibilityIcon color="primary" />,
            tooltipText: 'Show Task',
            onClick: () => setSelectedTaskId(row.id),
          },
          {
            label: 'Delete Task',
            icon: <DeleteIcon color="primary" />,
            tooltipText: 'Delete Task',
            onClick: () => {
              if (canDisableEditButton(row)) return;
              destroyTask({ variables: { id: row.id } });
            },
          },
        ]}
        onSelectionChange={(state) => console.log('ye aya onSelectionChange?', state)}
        onStateChange={(state) => console.log('ye aya onStateChange?', state)}
        toolbar={
          <Button onClick={() => setOpenedModals([...openedModal, 'CreateTask'])}>
            <AddBoxIcon fontSize="small" sx={{ mr: 1 }} />
            New
          </Button>
        }
      />

      {selectedTaskId && <ShowTaskModal handleClose={() => setSelectedTaskId(null)} taskId={selectedTaskId} />}
    </React.Fragment>
  );
};

export default TasksListTable;
